import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { withTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import { resolveDocumentLanguage } from '../../utils/languages';
import { useUserStore } from '../../store';
import axios from 'axios';
import BillingSettingsDialog from './BillingSettingsDialog';
import NewCustomerForm, { EMPTY_NEW_CUSTOMER, isNewCustomerValid } from '../shared/NewCustomerForm';
import { customerLabel } from '../../utils/customer';

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const CreateStandaloneInvoiceDialog = ({ open, onClose, onInvoiceCreated, t }) => {
  const currentOrganization = useUserStore((state) => state.currentOrganization);

  // State
  const [invoiceableMaintenances, setInvoiceableMaintenances] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingMaintenances, setLoadingMaintenances] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [language, setLanguage] = useState('fi');
  // While false the language follows the recipient; a manual pick freezes it.
  const [languageTouched, setLanguageTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);

  const [customerSearchInput, setCustomerSearchInput] = useState('');

  // New customer creation state
  const [customerMode, setCustomerMode] = useState('existing'); // 'existing' or 'new'
  const [saveCustomer, setSaveCustomer] = useState(true);
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // Load invoiceable maintenances
  const loadInvoiceableMaintenances = useCallback(async () => {
    setLoadingMaintenances(true);
    try {
      const response = await axios.get('/api/invoices/invoiceable-maintenances/');
      setInvoiceableMaintenances(response.data);
    } catch (err) {
      console.error('Error loading invoiceable maintenances:', err);
    } finally {
      setLoadingMaintenances(false);
    }
  }, []);

  // Load customers
  const loadCustomers = useCallback(async (query = '') => {
    setLoadingCustomers(true);
    try {
      const url = query
        ? `/api/customers/?search=${encodeURIComponent(query)}&typeahead=true`
        : '/api/customers/?page_size=50';
      const response = await axios.get(url);
      // Handle paginated response
      const customersData = response.data.results || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // The invoice is written in the recipient's language: what the customer asked for,
  // otherwise the workshop's invoice language. A manual pick wins over both.
  const recipient = selectedCustomer || selectedMaintenance?.customer || null;
  useEffect(() => {
    if (!open || languageTouched) return;
    setLanguage(resolveDocumentLanguage(recipient, currentOrganization));
  }, [open, languageTouched, recipient, currentOrganization]);

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedMaintenance(null);
      setSelectedCustomer(null);
      setLanguage(resolveDocumentLanguage(null, currentOrganization));
      setLanguageTouched(false);
      setNotes('');
      setError(null);
      setCustomerSearchInput('');
      setCustomerMode('existing');
      setSaveCustomer(true);
      setNewCustomer({ ...EMPTY_NEW_CUSTOMER });

      loadInvoiceableMaintenances();
      loadCustomers();
    }
  }, [open, loadInvoiceableMaintenances, loadCustomers]);

  // Debounced customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearchInput && customerSearchInput.length >= 2) {
        loadCustomers(customerSearchInput);
      } else if (!customerSearchInput) {
        loadCustomers();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearchInput, loadCustomers]);


  // Get display name for new customer preview
  const getNewCustomerDisplayName = () => {
    if (newCustomer.type === 'company') {
      return newCustomer.company_name?.trim() || '';
    }
    return `${newCustomer.first_name?.trim() || ''} ${newCustomer.last_name?.trim() || ''}`.trim();
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError(null);

      let customerId = selectedCustomer?.id;

      // If creating a new customer
      if (!selectedMaintenance && customerMode === 'new') {
        setCreatingCustomer(true);
        try {
          const customerPayload = {
            ...newCustomer,
            is_hidden: !saveCustomer,
          };
          const customerResponse = await axios.post('/api/customers/', customerPayload);
          customerId = customerResponse.data.id;
        } catch (customerErr) {
          const errorMessage = customerErr.response?.data?.non_field_errors?.[0] ||
            customerErr.response?.data?.detail ||
            Object.values(customerErr.response?.data || {}).flat().join(', ') ||
            t('Failed to create customer');
          setError(errorMessage);
          return;
        } finally {
          setCreatingCustomer(false);
        }
      }

      const payload = {
        language,
        notes,
      };

      if (selectedMaintenance) {
        payload.maintenance_id = selectedMaintenance.id;
      } else {
        payload.customer_id = customerId;
      }

      const response = await axios.post('/api/invoices/', payload);

      if (onInvoiceCreated) {
        onInvoiceCreated(response.data);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error;
      let translatedError;
      if (errorMessage) {
        if (errorMessage.includes('already exists')) {
          translatedError = t('An invoice already exists for this maintenance. Cancel the existing invoice first.');
        } else if (errorMessage.includes('IBAN is required')) {
          setBillingDialogOpen(true);
          return;
        } else if (errorMessage.includes('not found')) {
          translatedError = t('Customer not found');
        } else if (errorMessage.includes('Either maintenance_id or customer_id')) {
          translatedError = t('Please select a maintenance or customer');
        } else if (errorMessage.includes('Customer data contains values that are too long')) {
          translatedError = t('Customer data is too long for invoice. Please fix the customer phone number and business ID.');
        } else {
          translatedError = errorMessage;
        }
      } else {
        translatedError = t('Failed to create invoice');
      }
      setError(translatedError);
    } finally {
      setCreating(false);
    }
  };

  const handleBillingSaved = () => {
    setBillingDialogOpen(false);
    handleCreate();
  };

  const handleCloseDialog = () => {
    if (!creating && !creatingCustomer) {
      onClose();
    }
  };

  // Validation
  const canCreate = selectedMaintenance ||
    (customerMode === 'existing' && selectedCustomer) ||
    (customerMode === 'new' && isNewCustomerValid(newCustomer, { requireName: true }));
  const isStandalone = !selectedMaintenance;

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptIcon color="primary" />
        {t('Create invoice')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Maintenance Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('From maintenance (optional)')}</InputLabel>
          <Select
            value={selectedMaintenance?.id || ''}
            label={t('From maintenance (optional)')}
            onChange={(e) => {
              const maintenanceId = e.target.value;
              if (maintenanceId === '') {
                setSelectedMaintenance(null);
              } else {
                const maintenance = invoiceableMaintenances.find((m) => m.id === maintenanceId);
                setSelectedMaintenance(maintenance);
                // Clear customer selection when maintenance is selected
                setSelectedCustomer(null);
              }
            }}
            disabled={loadingMaintenances}
          >
            <MenuItem value="">
              <em>{t('Create empty invoice')}</em>
            </MenuItem>
            {invoiceableMaintenances.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                <Box>
                  <Typography variant="body2" component="span">
                    {m.vehicle?.registration_number || t('No vehicle')}
                  </Typography>
                  {m.customer && (
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                      - {m.customer.name}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                    ({formatCurrency(m.total_amount)})
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Customer Selection (only when no maintenance selected) */}
        {isStandalone && (
          <Box sx={{ mb: 2 }}>
            {/* Mode Toggle */}
            <ToggleButtonGroup
              value={customerMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setCustomerMode(newMode);
                  setError(null);
                }
              }}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="existing" sx={{ textTransform: 'none' }}>
                <PersonSearchIcon sx={{ mr: 1 }} />
                {t('Existing customer')}
              </ToggleButton>
              <ToggleButton value="new" sx={{ textTransform: 'none' }}>
                <AddIcon sx={{ mr: 1 }} />
                {t('New customer')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Existing Customer Selection */}
            {customerMode === 'existing' && (
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => customerLabel(option)}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                }}
                filterOptions={(x) => x}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setCustomerSearchInput(newInputValue);
                  }
                }}
                loading={loadingCustomers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('Customer')}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">
                        {customerLabel(option)}
                        {option.type === 'company' && (
                          <BusinessIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                        )}
                      </Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                noOptionsText={t('No customers found')}
              />
            )}

            {/* New Customer Form */}
            {customerMode === 'new' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <NewCustomerForm t={t} value={newCustomer} onChange={setNewCustomer} requireName />

                {/* Save customer checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveCustomer}
                      onChange={(e) => setSaveCustomer(e.target.checked)}
                    />
                  }
                  label={t('Save this customer for future use')}
                />

                {!saveCustomer && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {t('Customer will only be visible on this invoice')}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Summary */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('Invoice summary')}
          </Typography>

          {selectedMaintenance ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('Customer')}:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedMaintenance.customer?.name || t('Not assigned')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('Vehicle')}:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedMaintenance.vehicle?.registration_number || '-'}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('Parts')}:</Typography>
                <Typography variant="body2">{selectedMaintenance.parts_count} {t('items')}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('Jobs')}:</Typography>
                <Typography variant="body2">{selectedMaintenance.jobs_count} {t('items')}</Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {formatCurrency(selectedMaintenance.total_amount)}
                </Typography>
              </Box>
            </>
          ) : customerMode === 'existing' && selectedCustomer ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('Customer')}:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedCustomer.name}
                </Typography>
              </Box>

              {selectedCustomer.email && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('Email')}:</Typography>
                  <Typography variant="body2">{selectedCustomer.email}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Alert severity="info" sx={{ mt: 1 }}>
                {t('Empty invoice - add lines after creation')}
              </Alert>
            </>
          ) : customerMode === 'new' && isNewCustomerValid(newCustomer, { requireName: true }) ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">{t('Customer')}:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {getNewCustomerDisplayName()}
                  </Typography>
                  {!saveCustomer && (
                    <Chip label={t('One-time customer')} size="small" variant="outlined" color="info" />
                  )}
                </Box>
              </Box>

              {newCustomer.email && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('Email')}:</Typography>
                  <Typography variant="body2">{newCustomer.email}</Typography>
                </Box>
              )}

              {newCustomer.phone && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('Phone')}:</Typography>
                  <Typography variant="body2">{newCustomer.phone}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Alert severity="info" sx={{ mt: 1 }}>
                {t('Empty invoice - add lines after creation')}
              </Alert>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('Select a maintenance or customer to continue')}
            </Typography>
          )}
        </Paper>

        {/* Invoice Options */}
        <LanguageSelect
          fullWidth
          sx={{ mb: 2 }}
          value={language}
          label={t('Invoice language')}
          onChange={(e) => {
            setLanguageTouched(true);
            setLanguage(e.target.value);
          }}
        />

        <TextField
          label={t('Invoice notes')}
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('Optional notes to include on the invoice')}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseDialog} disabled={creating || creatingCustomer}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating || creatingCustomer || !canCreate}
          startIcon={(creating || creatingCustomer) ? <CircularProgress size={20} /> : <ReceiptIcon />}
        >
          {(creating || creatingCustomer) ? <span>{t('Creating...')}</span> : <span>{t('Create invoice')}</span>}
        </Button>
      </DialogActions>

      <BillingSettingsDialog
        open={billingDialogOpen}
        onClose={() => setBillingDialogOpen(false)}
        onSaved={handleBillingSaved}
      />
    </Dialog>
  );
};

export default withTranslation()(CreateStandaloneInvoiceDialog);
